/**
 * Quick View — Fetches product JSON + renders a modal with image, details,
 * variant picker, quantity selector and add-to-cart.
 *
 * Works with any element that has  data-quick-view-handle="<product-handle>"
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ */
  /* DOM references (cached once modal is rendered)                      */
  /* ------------------------------------------------------------------ */
  /** @type {HTMLElement | null} */
  var overlay = null;
  /** @type {HTMLElement | null} */
  var modal = null;
  /** @type {HTMLElement | null} */
  var loader = null;
  /** @type {HTMLElement | null} */
  var content = null;
  /** @type {HTMLImageElement | null} */
  var mainImg = null;
  /** @type {HTMLElement | null} */
  var thumbsWrap = null;
  /** @type {HTMLElement | null} */
  var vendorEl = null;
  /** @type {HTMLElement | null} */
  var titleEl = null;
  /** @type {HTMLElement | null} */
  var pricesEl = null;
  /** @type {HTMLElement | null} */
  var variantsEl = null;
  /** @type {HTMLInputElement | null} */
  var qtyInput = null;
  /** @type {HTMLButtonElement | null} */
  var atcBtn = null;
  /** @type {HTMLElement | null} */
  var atcText = null;
  /** @type {HTMLElement | null} */
  var atcSpinner = null;
  /** @type {HTMLAnchorElement | null} */
  var viewLink = null;

  /** @type {*} */
  var currentProduct = null;
  /** @type {*} */
  var currentVariant = null;
  /** @type {Record<string, string>} */
  var selectedOptions = {};
  /** @type {boolean} */
  var isOpen = false;
  /** @type {boolean} */
  var modalHandlersBound = false;

  /* ------------------------------------------------------------------ */
  /* Grab fresh DOM refs (called before every open to avoid stale refs) */
  /* ------------------------------------------------------------------ */
  function cacheRefs() {
    var prevModal = modal;
    overlay     = document.getElementById('quick-view-overlay');
    modal       = document.getElementById('quick-view-modal');
    loader      = document.getElementById('qv-loader');
    content     = document.getElementById('qv-content');
    mainImg     = /** @type {HTMLImageElement | null} */ (document.getElementById('qv-image'));
    thumbsWrap  = document.getElementById('qv-thumbnails');
    vendorEl    = document.getElementById('qv-vendor');
    titleEl     = document.getElementById('qv-title');
    pricesEl    = document.getElementById('qv-prices');
    variantsEl  = document.getElementById('qv-variants');
    qtyInput    = /** @type {HTMLInputElement | null} */ (document.getElementById('qv-qty'));
    atcBtn      = /** @type {HTMLButtonElement | null} */ (document.getElementById('qv-atc-btn'));
    atcText     = atcBtn ? atcBtn.querySelector('.qv-modal__atc-text') : null;
    atcSpinner  = atcBtn ? /** @type {HTMLElement | null} */ (atcBtn.querySelector('.qv-modal__atc-spinner')) : null;
    viewLink    = /** @type {HTMLAnchorElement | null} */ (document.getElementById('qv-view-link'));

    // If the modal element changed (e.g. DOM was re-rendered), reset handler binding flag
    if (prevModal && modal !== prevModal) {
      modalHandlersBound = false;
    }
  }

  /* ------------------------------------------------------------------ */
  /* Init — attach global listeners                                     */
  /* ------------------------------------------------------------------ */
  function init() {
    cacheRefs();

    // Register the trigger click listener in CAPTURE phase so it fires before the theme's
    // component framework (component.js) and before product-card / product-card-link handlers.
    // This is the single reliable way to intercept clicks on [data-quick-view-handle] buttons
    // that live inside Shopify custom-element product cards.
    document.addEventListener('click', function (e) {
      var target = e.target;
      // Walk composedPath for clicks inside shadow DOM
      if (e.composedPath && e.composedPath().length) {
        target = /** @type {EventTarget | null} */ (e.composedPath()[0]) || e.target;
      }
      if (!(target instanceof Element)) return;
      var trigger = /** @type {Element} */ (target).closest('[data-quick-view-handle]');
      if (!trigger) return;
      // Stop the event completely so no navigation or other click handler fires.
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      var handle = /** @type {HTMLElement} */ (trigger).dataset.quickViewHandle || '';
      if (handle) open(handle);
    }, true /* capture */);

    // Keyboard close (registered once on document)
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen) close();
    });

    // Bind modal-specific handlers (overlay close, qty, ATC)
    bindModalHandlers();
  }

  /* ------------------------------------------------------------------ */
  /* Bind handlers to modal elements (safe to call multiple times)      */
  /* ------------------------------------------------------------------ */
  function bindModalHandlers() {
    if (modalHandlersBound) return;
    if (!modal || !overlay) return;
    modalHandlersBound = true;

    // Close handlers
    overlay.addEventListener('click', close);
    var closeBtn = modal.querySelector('.qv-modal__close');
    if (closeBtn) closeBtn.addEventListener('click', close);

    // Quantity buttons
    modal.querySelectorAll('[data-qv-qty]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        if (!qtyInput) return;
        var cur = parseInt(qtyInput.value, 10) || 1;
        var htmlBtn = /** @type {HTMLElement} */ (btn);
        if (htmlBtn.dataset.qvQty === 'plus') qtyInput.value = String(cur + 1);
        else if (cur > 1) qtyInput.value = String(cur - 1);
      });
    });

    // Add to cart
    if (atcBtn) atcBtn.addEventListener('click', addToCart);
  }

  /* ------------------------------------------------------------------ */
  /* Open / close                                                       */
  /* ------------------------------------------------------------------ */
  /**
   * @param {string} handle
   */
  function open(handle) {
    if (!handle) return;

    // Re-cache DOM refs every time we open — guards against stale references
    // caused by section rendering, hydration, or Shopify theme editor updates.
    cacheRefs();

    if (!modal || !overlay) {
      console.warn('[Quick View] Modal elements not found in DOM. Ensure quick-view-modal snippet is rendered.');
      return;
    }

    // Ensure handlers are bound (handles late-init / re-rendered DOM)
    bindModalHandlers();

    isOpen = true;
    document.body.style.overflow = 'hidden';
    overlay.classList.add('qv-active');
    modal.classList.add('qv-active');
    modal.setAttribute('aria-hidden', 'false');
    overlay.setAttribute('aria-hidden', 'false');

    // Reset
    if (loader) loader.style.display = 'flex';
    if (content) content.style.display = 'none';
    if (qtyInput) qtyInput.value = '1';

    fetchProduct(handle);
  }

  function close() {
    isOpen = false;
    document.body.style.overflow = '';
    if (overlay) {
      overlay.classList.remove('qv-active');
      overlay.setAttribute('aria-hidden', 'true');
    }
    if (modal) {
      modal.classList.remove('qv-active');
      modal.setAttribute('aria-hidden', 'true');
    }
    currentProduct = null;
    currentVariant = null;
    selectedOptions = {};
  }

  /* ------------------------------------------------------------------ */
  /* Fetch product JSON via Shopify AJAX API                            */
  /* ------------------------------------------------------------------ */
  /**
   * @param {string} handle
   */
  function fetchProduct(handle) {
    fetch('/products/' + handle + '.js')
      .then(function (r) {
        if (!r.ok) throw new Error('Product not found');
        return r.json();
      })
      .then(function (product) {
        currentProduct = product;
        renderProduct(product);
      })
      .catch(function (err) {
        console.error('[Quick View]', err);
        close();
      });
  }

  /* ------------------------------------------------------------------ */
  /* Render product data into the modal                                 */
  /* ------------------------------------------------------------------ */
  /**
   * @param {*} product
   */
  function renderProduct(product) {
    // Title & vendor
    if (titleEl) titleEl.textContent = product.title;
    if (vendorEl) {
      vendorEl.textContent = product.vendor || '';
      vendorEl.style.display = product.vendor ? '' : 'none';
    }

    // Link
    if (viewLink) viewLink.href = product.url;

    // Images
    renderImages(product);

    // Price — pick first available variant
    var firstAvailable = product.variants.find(/** @param {*} v */ function (v) { return v.available; }) || product.variants[0];
    selectVariant(firstAvailable);

    // Variants
    renderVariants(product);

    // Show content
    if (loader) loader.style.display = 'none';
    if (content) content.style.display = 'grid';
  }

  /* ---- Images ---- */
  /**
   * @param {*} product
   */
  function renderImages(product) {
    if (!thumbsWrap || !mainImg) return;
    thumbsWrap.innerHTML = '';
    if (!product.media || product.media.length === 0) {
      if (product.featured_image) {
        mainImg.src = getSizedImageUrl(product.featured_image, '800x');
        mainImg.alt = product.title;
      }
      return;
    }

    // Filter to images only
    var images = product.media.filter(/** @param {*} m */ function (m) { return m.media_type === 'image'; });
    if (images.length === 0 && product.featured_image) {
      mainImg.src = getSizedImageUrl(product.featured_image, '800x');
      mainImg.alt = product.title;
      return;
    }

    mainImg.src = getSizedImageUrl(images[0].src, '800x');
    mainImg.alt = images[0].alt || product.title;

    var currentMainImg = mainImg;
    var currentThumbsWrap = thumbsWrap;

    if (images.length > 1) {
      images.forEach(/** @param {*} img @param {number} idx */ function (img, idx) {
        var thumb = document.createElement('button');
        thumb.type = 'button';
        thumb.className = 'qv-modal__thumb' + (idx === 0 ? ' qv-thumb-active' : '');
        thumb.innerHTML = '<img src="' + getSizedImageUrl(img.src, '120x') + '" alt="' + (img.alt || '') + '">';
        thumb.addEventListener('click', function () {
          currentMainImg.src = getSizedImageUrl(img.src, '800x');
          currentMainImg.alt = img.alt || product.title;
          currentThumbsWrap.querySelectorAll('.qv-modal__thumb').forEach(function (t) { t.classList.remove('qv-thumb-active'); });
          thumb.classList.add('qv-thumb-active');
        });
        currentThumbsWrap.appendChild(thumb);
      });
    }
  }

  /* ---- Variant options ---- */
  /**
   * @param {*} product
   */
  function renderVariants(product) {
    if (!variantsEl) return;
    variantsEl.innerHTML = '';

    if (product.variants.length <= 1 && (!product.options || product.options.length === 0 || (product.options.length === 1 && product.options[0].values.length <= 1))) {
      return; // Single variant, no options to show
    }

    // Initialise selectedOptions from the first available variant
    var firstAvailable = product.variants.find(/** @param {*} v */ function (v) { return v.available; }) || product.variants[0];
    product.options.forEach(/** @param {*} opt @param {number} idx */ function (opt, idx) {
      selectedOptions[opt.name] = firstAvailable.options[idx];
    });

    var currentVariantsEl = variantsEl;
    product.options.forEach(/** @param {*} option @param {number} optionIndex */ function (option, optionIndex) {
      var group = document.createElement('div');
      group.className = 'qv-modal__option-group';

      var label = document.createElement('span');
      label.className = 'qv-modal__option-label';
      label.textContent = option.name + ': ' + (selectedOptions[option.name] || '');
      group.appendChild(label);

      var valuesWrap = document.createElement('div');
      valuesWrap.className = 'qv-modal__option-values';

      // Deduplicate values
      /** @type {Record<string, boolean>} */
      var seen = {};
      option.values.forEach(/** @param {string} val */ function (val) {
        if (seen[val]) return;
        seen[val] = true;

        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'qv-modal__option-btn';
        btn.textContent = val;

        if (selectedOptions[option.name] === val) {
          btn.classList.add('qv-option-active');
        }

        btn.addEventListener('click', function () {
          selectedOptions[option.name] = val;

          // Update active state on buttons in this group
          valuesWrap.querySelectorAll('.qv-modal__option-btn').forEach(function (b) { b.classList.remove('qv-option-active'); });
          btn.classList.add('qv-option-active');

          // Update label
          label.textContent = option.name + ': ' + val;

          // Find matching variant
          var matched = findVariant(product);
          if (matched) {
            selectVariant(matched);
            // Update image if variant has one
            if (matched.featured_image && mainImg) {
              mainImg.src = getSizedImageUrl(matched.featured_image.src, '800x');
              mainImg.alt = matched.featured_image.alt || product.title;
            }
          }

          // Re-render all option groups for availability
          updateOptionAvailability(product);
        });

        valuesWrap.appendChild(btn);
      });

      group.appendChild(valuesWrap);
      currentVariantsEl.appendChild(group);
    });

    updateOptionAvailability(product);
  }

  /* ---- Find variant matching selectedOptions ---- */
  /**
   * @param {*} product
   * @returns {*}
   */
  function findVariant(product) {
    return product.variants.find(/** @param {*} v */ function (v) {
      return product.options.every(/** @param {*} opt @param {number} idx */ function (opt, idx) {
        return v.options[idx] === selectedOptions[opt.name];
      });
    }) || null;
  }

  /* ---- Update option availability ---- */
  /**
   * @param {*} product
   */
  function updateOptionAvailability(product) {
    if (!variantsEl) return;
    var groups = variantsEl.querySelectorAll('.qv-modal__option-group');
    groups.forEach(function (group, optionIndex) {
      var buttons = group.querySelectorAll('.qv-modal__option-btn');
      buttons.forEach(function (btn) {
        var testOptions = Object.assign({}, selectedOptions);
        testOptions[product.options[optionIndex].name] = btn.textContent || '';

        // Check if any variant with this option combo is available
        var hasAvailable = product.variants.some(/** @param {*} v */ function (v) {
          return product.options.every(/** @param {*} opt @param {number} idx */ function (opt, idx) {
            return v.options[idx] === testOptions[opt.name];
          }) && v.available;
        });

        btn.classList.toggle('qv-option-unavailable', !hasAvailable);
      });
    });
  }

  /* ---- Select a variant and update price / ATC ---- */
  /**
   * @param {*} variant
   */
  function selectVariant(variant) {
    currentVariant = variant;
    renderPrice(variant);
    updateAtcButton(variant);
  }

  /* ---- Render price ---- */
  /**
   * @param {*} variant
   */
  function renderPrice(variant) {
    if (!variant) return;
    var html = '';

    if (variant.compare_at_price && variant.compare_at_price > variant.price) {
      html += '<span class="qv-modal__price--sale">' + formatMoney(variant.price) + '</span>';
      html += '<span class="qv-modal__price--compare">' + formatMoney(variant.compare_at_price) + '</span>';
      var discount = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
      html += '<span class="qv-modal__price--discount">-' + discount + '%</span>';
    } else {
      html += '<span>' + formatMoney(variant.price) + '</span>';
    }

    if (pricesEl) pricesEl.innerHTML = html;
  }

  /* ---- Update ATC button ---- */
  /**
   * @param {*} variant
   */
  function updateAtcButton(variant) {
    if (!atcBtn || !atcText) return;
    if (!variant || !variant.available) {
      atcBtn.disabled = true;
      atcBtn.classList.add('qv-sold-out');
      atcText.textContent = variant ? 'Sold out' : 'Unavailable';
    } else {
      atcBtn.disabled = false;
      atcBtn.classList.remove('qv-sold-out');
      atcText.textContent = 'Add to cart';
    }
  }

  /* ------------------------------------------------------------------ */
  /* Add to Cart via AJAX                                               */
  /* ------------------------------------------------------------------ */
  function addToCart() {
    if (!currentVariant || !currentVariant.available || !atcBtn || !atcText || !atcSpinner || atcBtn.disabled) return;

    atcBtn.disabled = true;
    atcText.style.display = 'none';
    atcSpinner.style.display = '';

    var qty = parseInt(qtyInput ? qtyInput.value : '1', 10) || 1;

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({
        items: [{ id: currentVariant.id, quantity: qty }]
      })
    })
    .then(function (r) {
      if (!r.ok) return r.json().then(function (d) { throw new Error(d.description || 'Error'); });
      return r.json();
    })
    .then(function () {
      if (!atcText || !atcSpinner || !atcBtn) return;
      atcText.textContent = 'Added!';
      atcText.style.display = '';
      atcSpinner.style.display = 'none';
      atcBtn.disabled = false;

      // Refresh cart count
      refreshCartBubble();

      // Dispatch cart update event for theme compatibility
      document.dispatchEvent(new CustomEvent('cart:updated'));

      setTimeout(function () {
        if (atcText) atcText.textContent = 'Add to cart';
        close();
      }, 1200);
    })
    .catch(function (err) {
      if (!atcText || !atcSpinner || !atcBtn) return;
      atcText.textContent = err.message || 'Error';
      atcText.style.display = '';
      atcSpinner.style.display = 'none';
      atcBtn.disabled = false;
      setTimeout(function () { updateAtcButton(currentVariant); }, 2000);
    });
  }

  /* ---- Refresh cart bubble count ---- */
  function refreshCartBubble() {
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(/** @param {*} cart */ function (cart) {
        // Update any cart count elements
        document.querySelectorAll('[data-cart-count], .cart-count-bubble span, cart-icon .icon-count').forEach(function (el) {
          el.textContent = cart.item_count;
        });
        // Dispatch an event
        document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { cart: cart } }));
      })
      .catch(function () {});
  }

  /* ------------------------------------------------------------------ */
  /* Helpers                                                            */
  /* ------------------------------------------------------------------ */

  /**
   * Format price in cents to a money string using the shop's format.
   * @param {number | string} cents
   * @returns {string}
   */
  function formatMoney(cents) {
    if (typeof cents === 'string') cents = parseInt(cents.replace('.', ''), 10) || 0;

    var amount = (/** @type {number} */ (cents) / 100).toFixed(2);

    // Try to use the Shopify money format
    if (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) {
      // Use the global money format if available
    }

    // Attempt to use theme's money_format if available
    var format = '';
    var win = /** @type {*} */ (window);
    if (win.theme && win.theme.moneyFormat) {
      format = win.theme.moneyFormat;
    } else if (win.Shopify && win.Shopify.money_format) {
      format = win.Shopify.money_format;
    }

    if (format) {
      return format
        .replace(/\{\{\s*amount\s*\}\}/, amount)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/, parseInt(amount, 10).toString())
        .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, amount.replace('.', ','))
        .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/, parseInt(amount, 10).toString());
    }

    // Fallback — detect the currency symbol from the page
    var currencySym = '';
    var priceEl = document.querySelector('[data-product-price], .product-card__price--sale, .fct-card__price--sale, .price-item--sale, .price .money');
    if (priceEl && priceEl.textContent) {
      var priceText = priceEl.textContent.trim();
      var match = priceText.match(/^([^\d]*)/);
      if (match && match[1]) currencySym = match[1].trim() + ' ';
    }
    if (!currencySym) currencySym = '₹';

    return currencySym + amount;
  }

  /**
   * Get a Shopify CDN image URL at a specific size.
   * @param {string} url
   * @param {string} size
   * @returns {string}
   */
  function getSizedImageUrl(url, size) {
    if (!url) return '';
    // If it's already a CDN URL with size params, return as-is for featured_image string
    if (typeof url === 'string' && url.indexOf('cdn.shopify') !== -1) {
      // Remove existing size from URL, then add new one
      var clean = url.replace(/_(pico|icon|thumb|small|compact|medium|large|grande|original|\d{1,4}x\d{0,4}|x\d{1,4})\./i, '.');
      var dotIndex = clean.lastIndexOf('.');
      if (dotIndex !== -1) {
        return clean.substring(0, dotIndex) + '_' + size + clean.substring(dotIndex);
      }
    }
    return url;
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                               */
  /* ------------------------------------------------------------------ */

  // Expose open() globally so buttons can call window.__qvOpen(handle) directly
  // as a belt-and-suspenders fallback alongside the capture listener.
  /** @type {*} */ (window).__qvOpen = open;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
